import { lazy, Suspense } from "react";
import { Music2, LogOut, Coins, History, Settings as SettingsIcon, Play, Trophy, Sparkles } from "lucide-react";

import { PlaybackPreferenceToggle } from "@/components/PlaybackPreferenceToggle";
// NotificationCenter — single bell that consolidates the previously-
// inline Daily Mix / Recently Saved / Achievements pills. Lazy-loaded
// because it pulls in useDailyMix + useAchievements hooks (heavy when
// rendered to non-Pro / logged-out users). 2026-05-14 — replaces the
// 3 inline pill divs.
const NotificationCenter = lazy(() =>
  import("@/components/NotificationCenter").then((m) => ({ default: m.NotificationCenter })),
);
// NAV_GROUPS is dynamically imported when the admin dropdown opens — see
// `useEffect` below. Static import was pulling AdminNavigation's 28+ admin
// icons + dropdown JSX into the main bundle for every visitor, even though
// the data is only rendered inside `{isAdmin && ...}`. Lazy-load saves ~30KB
// raw / ~8KB gzip on the public-route bundle. (Andy 2026-05-08 perf pass.)
type NavGroups = typeof import("@/components/AdminNavigation").NAV_GROUPS;
import { LogoEqualizer } from "@/components/LogoVariants";
import { MobileNavSheet } from "@/components/MobileNavSheet";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { useSpotifyPlayerContext } from "@/contexts/SpotifyPlayerContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";


import { Separator } from "@/components/ui/separator";
import { trackNavigation, trackAuth } from "@/lib/analytics";
import { useState, useEffect, useMemo, memo } from "react";
import { useUserRole } from "@/hooks/useUserRole";

// UnseenReconciliationBadge hosts the useUnseenReconciliationGrants hook
// (141 lines) and the popover JSX. Lazy-loaded so logged-out users don't
// download it. Mounted only when user is signed in. Returns null internally
// when there are no unseen grants.
const UnseenReconciliationBadge = lazy(() =>
  import("@/components/UnseenReconciliationBadge"),
);

// Links that `navLinks` carries for the mobile drawer but the desktop row
// cannot fit. See the comment on the desktop <nav> for the measurement.
const DESKTOP_NAV_OMIT = new Set<string>(["/how-it-works"]);

export const Header = memo(function Header() {
  const { user, profile, isPro, signOut } = useAuth();
  const { balance, loading: creditsLoading } = useCredits();
  const { role } = useUserRole();
  const { currentPlaylist, maximize } = useSpotifyPlayerContext();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // S16-043 followup — pulse the Coins badge when there are unacknowledged
  // auto-reconciliation grants. The hook + popover JSX moved into
  // UnseenReconciliationBadge.tsx (lazy-loaded below) so logged-out visitors
  // don't pay for the 141-line hook + supabase polling effect.

  const isAdmin = role === "admin";

  // Lazy-load admin nav groups — only when the user is actually admin.
  // The dropdown reveals admin items conditionally; we gate the chunk fetch
  // to match. Non-admin visitors never download the AdminNavigation module.
  const [navGroups, setNavGroups] = useState<NavGroups | null>(null);
  useEffect(() => {
    if (!isAdmin || navGroups) return;
    let cancelled = false;
    void import("@/components/AdminNavigation").then((m) => {
      if (!cancelled) setNavGroups(m.NAV_GROUPS);
    });
    return () => { cancelled = true; };
  }, [isAdmin, navGroups]);

  // The coin toast that used to live here has been removed, and must not be
  // rebuilt the way it was.
  //
  // It listened for an INSERT on credits_ledger. That table was taken out of
  // the supabase_realtime publication on 2026-02-17 by migration
  // 20260217220000_phase4_database_schema_fixes.sql, item H16: "Financial
  // transaction data should not be broadcast to clients." The subscription
  // outlived the decision, so no coin toast has fired since that date.
  //
  // Republishing credits_ledger would reverse a deliberate security call, so
  // any replacement would have to fire from the action that changes the
  // balance rather than from a broadcast of ledger rows.
  //
  // DECIDED 2026-08-19: do not rebuild it. Measured over the 30 days to that
  // date, credits_ledger took 461 rows, of which 316 (69%) were the QA
  // harness writing qa_release_smoke_adjustment. The 145 real rows came from
  // TWO users, and 144 of them were pro_playlist_generation, a spend of one
  // coin. Exactly one grant occurred in the whole window.
  //
  // So the toast would almost only ever say "1 coin used", to someone who
  // just pressed Generate and is watching the generation run. That is noise
  // on an action they initiated, and the header balance now reflects it
  // anyway: useCredits refreshes every mounted instance, so this header
  // updates whenever any surface calls refetch.
  //
  // The case genuinely worth surfacing is the opposite one, coins arriving
  // while the user was not looking, and a toast is the wrong shape for it
  // because a toast the user missed is gone. That case already has a better
  // home: UnseenReconciliationBadge, rendered below, pulses until tapped.


  const handleSignOut = () => {
    trackAuth('sign_out');
    signOut();
  };

  const navLinks = useMemo(() => [
    { to: "/", label: "Home", onClick: () => trackNavigation('home') },
    { to: "/how-it-works", label: "How It Works", onClick: () => trackNavigation('how_it_works') },
    { to: "/my-vibe", label: "My Vibe", onClick: () => trackNavigation('my_vibe') },
    { to: "/generate", label: "Generate", onClick: () => trackNavigation('generate') },
    { to: "/playlists", label: "Playlists", onClick: () => trackNavigation('playlists') },
    { to: "/community", label: "Community", onClick: () => trackNavigation('community') },
    { to: "/convert", label: "Converter", onClick: () => trackNavigation('converter') },
    { to: "/blog", label: "Blog", onClick: () => trackNavigation('blog') },
    { to: "/pricing", label: "Pricing", onClick: () => trackNavigation('pricing') },
  ], []);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 glass-card md:backdrop-blur-xl shadow-header bg-background/90 md:bg-background/40">
      {/* Skip to main content - Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-100 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:font-semibold"
      >
        Skip to main content
      </a>
      <div className="container flex flex-nowrap h-16 items-center justify-between relative z-10 gap-2">
        <Link
          to="/"
          onClick={() => trackNavigation('home', 'logo')}
          // relative tap-target + py-2: the logo mark renders 28px tall
          // (measured) inside the 64px row, so the link was a 28px touch
          // target. py-2 takes it to 44px of real height and the coarse-pointer
          // ::after guarantees the 44x44 floor (design-rules §10). Both fit the
          // row with 20px to spare, so nothing moves.
          className="relative tap-target py-2 hover:opacity-80 transition-opacity focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
          aria-label="Spotivibly homepage"
        >
          <LogoEqualizer size="sm" />
        </Link>

        {/* xl, not lg. Measured 2026-08-21 at a 1040px viewport: the bar needs
            1074px to fit on one line (logo 180 + nav 735 + right cluster 79,
            plus 16px of gaps and 64px of container padding) and the nav used to
            switch on at lg = 1024. In that band flex-nowrap gave the nav its
            full 735px and squeezed the right cluster from its natural 79px down
            to 33px, so the Sign In button rendered at 964-1043 against a 1040
            viewport and the page picked up a horizontal scrollbar.

            1074 is the SIGNED-OUT floor and the friendlier one. A signed-in
            header carries the Spotify status dot, notifications, the coins chip
            and the avatar in that same cluster, so its requirement is
            materially higher — which is how this surfaced, on a signed-in
            window around 1072px with the coins chip cut in half.

            xl (1280) clears both with room rather than sitting on the edge of
            the measured number, since the cluster's width varies with auth
            state, plan and notification count. The cost is that 1024-1279 now
            gets the sheet instead of the inline nav; a clipped control is worse
            than a hamburger, and MobileNavSheet's trigger moves to xl:hidden in
            the same change so the two can never both hide.

            The cheaper alternative, kept for later: the nav's 735px is 112px of
            gap-4 across seven gaps, so tightening to gap-2 below xl reclaims
            ~56px and would fit the signed-out case at lg. That is an IA call
            about nav density, not a bug fix, so it is not made here. */}
        <nav className="hidden xl:flex items-center gap-4 whitespace-nowrap" aria-label="Main navigation">
          {/* One source of truth for both nav surfaces. This row used to be
              eight hand-written <Link> blocks while the drawer mapped
              `navLinks`, so the two drifted: How It Works reached the drawer
              and never the desktop (design-rules §0, controls of the same kind
              behave identically). Mapping the same array means a new entry
              lands on both surfaces automatically.

              DESKTOP_NAV_OMIT is the one deliberate exception, and it is a
              width fact rather than a taste call. Measured on prod at the lg
              breakpoint (1024px): the row is 1012px wide and carries logo 180
              + nav 695 + right cluster 57, leaving 64px of slack — and a
              "How It Works" link measures 121px plus its 16px gap. Adding it
              overflows the row by 73px, which the §4 slack rule (2026-07-21)
              calls unshippable. Whether the desktop row earns a ninth item —
              by shortening a label, dropping one, or moving the tail into a
              "More" menu — is a design call, not a polish edit. */}
          {navLinks
            .filter((link) => !DESKTOP_NAV_OMIT.has(link.to))
            .map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={(event) => {
                  link.onClick();
                  // Clicking Generate while already on /generate resets the
                  // form instead of navigating to the page you are on.
                  if (link.to === "/generate" && location.pathname === "/generate") {
                    event.preventDefault();
                    window.dispatchEvent(new CustomEvent("spotivibly:generate-reset-request"));
                  }
                }}
                className="text-sm font-medium uppercase tracking-[0.08em] text-foreground/80 hover:text-foreground transition-colors duration-300 relative group focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
              >
                <span className="relative z-10">{link.label}</span>
                <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-linear-to-r from-primary to-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </Link>
            ))}
        </nav>

        {/* Right cluster - gap tightens to 2 (8px) on mobile so the
            +1 icon from the always-visible bell (2026-05-14 PM) doesn't
            push the avatar past the viewport edge at narrow widths.
            sm:gap-3 restores breathing room ≥640px. */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Mobile Menu */}
          <MobileNavSheet
            open={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
            user={user}
            navLinks={navLinks}
            balance={balance}
            creditsLoading={creditsLoading}
            onSignOut={handleSignOut}
          />

          {/* Now Playing Button (when playlist is active) */}
          {currentPlaylist && (
            <Button
              variant="ghost"
              size="icon"
              onClick={maximize}
              className="relative rounded-full hover:bg-primary/20 transition-colors duration-200 animate-fade-in"
              aria-label={`Now playing: ${currentPlaylist.name}`}
            >
              <Play className="h-5 w-5 text-primary fill-primary motion-safe:animate-pulse" />
            </Button>
          )}
          
          {/* Playback primary preference - Spotify or YouTube. Hidden on
              small screens (header is too tight); the same toggle is
              accessible from Account → Preferences. Andy 2026-05-10. */}
          {user && (
            <div className="hidden lg:inline-flex">
              <PlaybackPreferenceToggle />
            </div>
          )}

          {/* NotificationCenter - single bell that consolidates the three
              previously-inline pills (Daily Mix, Recently Saved,
              Achievements). Logged-in only.
              2026-05-14 PM (Andy): "i don't see the notifications on
              mobile". The previous `hidden md:inline-flex` gate hid
              the bell on mobile entirely - and MobileNavSheet never
              got the consolidated notifications surface that was
              promised. Now visible on every viewport; the mobile right
              cluster comfortably fits hamburger + bell + avatar at
              ≥360px (coin badge stays `hidden sm:flex` and lives in
              the hamburger drawer). */}
          {user && (
            <Suspense fallback={null}>
              <NotificationCenter isPro={isPro} />
            </Suspense>
          )}

          {/* Credits Badge (only when logged in). Pulses when there are
              unacknowledged auto-reconciliation grants. Click behavior:
              - With pulse: opens an in-place popover explaining the recent
                credit (which generation, how many coins). Dismiss button
                silences the pulse. No navigation.
              - Without pulse: navigates to /buy-coins (purchase flow).
              Andy 2026-05-06: 'Just show a message when clicked or
              something' - don't kick users to the buy-coins page when the
              reason for clicking was 'why is this pulsing'. */}
          {/* Coin balance - both states (unseen-grants pulse + plain link)
              live inside the lazy UnseenReconciliationBadge. Only mounted for
              logged-in users. Logged-out visitors skip the chunk entirely.
              Wrap in `hidden sm:inline-flex` so the empty 0-width Link
              wrapper (the inner Badge is `hidden sm:flex` itself, but the
              outer <Link>/<button> still occupied a flex gap slot) is
              completely removed from layout below sm. 2026-05-14 PM. */}
          {user && (
            <div className="hidden sm:inline-flex">
              <Suspense fallback={null}>
                <UnseenReconciliationBadge
                  userId={user.id}
                  balance={balance}
                  creditsLoading={creditsLoading}
                />
              </Suspense>
            </div>
          )}
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* No scale/transition overrides: the ghost variant already
                    ships hover:scale-[1.02] active:scale-[0.98] and the base
                    already ships the transition. Re-declaring them here handed
                    tailwind-merge two competing hover scales and replaced the
                    base transition list with transition-transform, so the
                    variant's own colour change stopped easing (design-rules
                    §5 — don't fight the variant). Only the size stays. */}
                <Button variant="ghost" size="icon" className="rounded-full h-11 w-11 sm:h-10 sm:w-10" aria-label="User menu">
                  <Avatar className="h-8 w-8 ring-2 ring-primary/20 hover:ring-primary/40 transition-shadow duration-200">
                    <AvatarImage src={profile?.avatar_url || ""} />
                    <AvatarFallback className="bg-primary/10 text-accent font-semibold hover:bg-primary/20 hover:text-primary-foreground transition-colors">
                      {profile?.display_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card md:bg-card/95 md:backdrop-blur-xl border-border/50 w-56">
                <DropdownMenuItem asChild>
                  <Link
                    to="/account"
                    onClick={() => trackNavigation('account')}
                    className="cursor-pointer flex items-center"
                  >
                    <Music2 className="mr-2 h-4 w-4" />
                    Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/profile"
                    onClick={() => trackNavigation('music_profile')}
                    className="cursor-pointer flex items-center"
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Musical Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link 
                    to="/buy-coins" 
                    onClick={() => trackNavigation('buy_coins')}
                    className="cursor-pointer flex items-center"
                  >
                    <Coins className="mr-2 h-4 w-4" />
                    Buy Coins
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/coins-history"
                    onClick={() => trackNavigation('coins_history')}
                    className="cursor-pointer flex items-center"
                  >
                    <History className="mr-2 h-4 w-4" />
                    Coins History
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/achievements"
                    onClick={() => trackNavigation('genre_explorer')}
                    className="cursor-pointer flex items-center"
                  >
                    <Trophy className="mr-2 h-4 w-4" />
                    Genre Explorer
                  </Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/settings"
                      onClick={() => trackNavigation('settings')}
                      className="cursor-pointer flex items-center"
                    >
                      <SettingsIcon className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                )}

                {isAdmin && navGroups && (
                  // Admin section — sourced from AdminNavigation.NAV_GROUPS
                  // so the Header dropdown and the in-page admin nav can
                  // never drift again. Pre-2026-05-07 the two had grown
                  // apart: Header was missing Homepage Carousel + Tracking,
                  // AdminNavigation was missing YouTube Conversions.
                  // navGroups is null until the lazy chunk loads (see useEffect
                  // above). For admins, this typically takes <100ms after the
                  // dropdown first opens; for non-admins, it never loads.
                  <>
                    {Object.entries(navGroups).map(([groupKey, group]) => (
                      <div key={groupKey}>
                        <Separator className="my-1" />
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {group.label}
                        </div>
                        {group.items.map((item) => (
                          <DropdownMenuItem key={item.id} asChild>
                            <Link
                              to={item.path}
                              onClick={() => trackNavigation(`admin_${item.id}`)}
                              className="cursor-pointer flex items-center"
                            >
                              <span className="mr-2 inline-flex">{item.icon}</span>
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ))}
                  </>
                )}
                
                <Separator className="my-1" />
                <DropdownMenuItem onClick={handleSignOut} className="flex items-center">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              asChild
              variant="default"
              // Comfortable on mobile (44×, matches avatar height + clears
              // design-rules §10 a11y tap-target floor), comfortable on sm+.
              // Earlier Andy-direction (2026-05-14 PM "compact, fix globally")
              // had us at h-9 (36px) on mobile which read tight but failed
              // the 44px floor. The avatar in the logged-in cluster sits at
              // h-11 (44px) mobile, so logged-out [hamburger + Sign In]
              // diverged 8px from logged-in [hamburger + avatar]. Tightened
              // px-3 (mobile) is what gives the cluster breathing room, not
              // the height — keep narrow padding, bump height back to match.
              // (2026-05-21 polish round 3.)
              size="sm"
              // Height/padding only. The scale + transition overrides that
              // used to sit here fought the default variant's own
              // hover:scale-[1.02]/active:scale-[0.98], and `transition-transform`
              // narrowed the base transition list so hover:bg-primary/90
              // snapped instead of easing (design-rules §5).
              className="h-11 px-3 sm:h-10 sm:px-4 font-semibold"
            >
              <Link
                to="/auth"
                state={{ from: location }}
                onClick={() => trackNavigation('auth')}
              >
                Sign In
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
});
